import type { EditorHost } from '@blocksuite/affine/std';
import {
  type GfxController,
  GfxControllerIdentifier,
} from '@blocksuite/affine/std/gfx';
import type { Store } from '@blocksuite/affine/store';
import { getCommonBound } from '@blocksuite/global/gfx';

import { EdgeOrchestrator } from './orchestration/EdgeOrchestrator';

interface ReadonlyEdgeEditor extends ParentNode {
  host?: EditorHost | null;
}

/**
 * Mounts derived StreamLain edges for a detached, readonly edgeless editor.
 *
 * Normal document editors receive this runtime from ReactiveEdgeService. Preview
 * editors intentionally live outside that document-service lifecycle, so they
 * need the same derivation/rendering pipeline without a remote sync handshake or
 * workspace-wide database discovery.
 */
export function mountReadonlyEdgelessEdgeRuntime({
  editor,
  store,
}: {
  editor: ReadonlyEdgeEditor;
  store: Store;
}): (() => void) | undefined {
  const gfx = editor.host?.std.getOptional(GfxControllerIdentifier);
  const rootId = store.root?.id;
  const edgeContainer = findInComposedTree(editor, '.edgeless-container');

  if (!gfx || !rootId || !(edgeContainer instanceof HTMLElement)) {
    return;
  }

  const orchestrator = new EdgeOrchestrator({
    gfx,
    store,
    rootId,
    container: edgeContainer,
    currentDocId: store.id,
    // Detached preview stores are already materialized and readonly. They have
    // no DocService sync lifecycle and must never wait for a remote handshake.
    waitForInitialSync: async () => {},
    // A preview only renders edges derivable from its own immutable snapshot.
    getWorkspaceDatabases: () => [],
  });
  orchestrator.initialize();

  return () => orchestrator.dispose();
}

/**
 * Fits an immutable preview to its viewport without the normal editor's 100%
 * zoom ceiling. A compact document should use the available preview area just
 * like an oversized document should be scaled down to fit it. The small
 * preview overscan keeps the fitted content visually prominent.
 */
export function fitReadonlyEdgelessViewport(
  gfx: GfxController,
  padding: [number, number, number, number] = [48, 48, 48, 48]
): void {
  const viewport = gfx.viewport;
  viewport.onResize();
  const commonBound = getCommonBound(
    gfx.viewportBoundElements.map(element => element.elementBound)
  );
  const { zoom, centerX, centerY } = viewport.getFitToScreenData(
    commonBound,
    padding,
    undefined,
    0
  );
  viewport.setViewport(zoom * 1.4, [centerX, centerY], false);
}

export function findInComposedTree(
  root: ParentNode,
  selector: string
): Element | null {
  const direct = root.querySelector(selector);
  if (direct) return direct;
  for (const element of root.querySelectorAll('*')) {
    if (element.shadowRoot) {
      const nested = findInComposedTree(element.shadowRoot, selector);
      if (nested) return nested;
    }
  }
  return null;
}
